import { useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { MapPin, Check, ChevronDown, Search, X, RefreshCw, Clock3 } from "lucide-react";
import { toast } from "react-toastify";
import { useStores } from "../../hooks/useStores";
import {
  getStoresRefreshStatus,
  refreshStoresCatalog,
  formatRefreshCooldown,
  RefreshCooldownError,
  type RetailerWithStores,
} from "../../utils/storesLoader";
import { useUserContext } from "../../context/UserContext";
import { geocodeZipCode } from "../../utils/geocoding";
import { findDollarGeneralStoreInList, type DollarGeneralStore } from "../../utils/storeUtils";
import { allottedStoreIds, getStoreChangeWarning, getWeeklyStoreChangeLock, parseStoreChangeError } from "../../utils/userUtils";
import type { RetailerType } from "../../config/retailers";
import { getStoreActivity } from "../../services/StoreActivityService";
import {
  formatStoreIdsForSave,
  getStoreData,
  normalizeStore,
  calculateDistance,
  storeActivityLabel,
  type Store,
  type StoreWithDistance,
} from "./StoresFilter.utils";
import { useTranslation } from "../../context/LanguageContext";
import ZipCodeDisplay from "../ZipCodeDisplay";

interface StoresFilterProps {
  selectedStores: string[];
  storeLimit: number;
  retailer: string;
  loadedStores?: Store[] | null;
  onStoreChange?: (stores: string[]) => Promise<void>;
}

function buildStoreUpdatePayload(
  retailer: string,
  selectedStores: string[],
  timestamp: number
) {
  switch (retailer) {
    case "walmart":
      return { storeIds: selectedStores, lastStoreChange: timestamp };
    case "home_depot":
      return { homeDepotStoreIds: selectedStores, lastHomeDepotStoreChange: timestamp };
    case "target":
      return { targetStoreIds: selectedStores, lastTargetStoreChange: timestamp };
    case "dollar_general":
      return { dollarGeneralStoreIds: selectedStores, lastDollarGeneralStoreChange: timestamp };
    case "costco":
      return { costcoStoreIds: selectedStores, lastCostcoStoreChange: timestamp };
    case "lowes":
      return { lowesStoreIds: selectedStores, lastLowesStoreChange: timestamp };
    case "tractor_supply":
      return { tractorSupplyStoreIds: selectedStores, lastTractorSupplyStoreChange: timestamp };
    case "northern_tool":
      return { northernToolStoreIds: selectedStores, lastNorthernToolStoreChange: timestamp };
    case "samsclub":
      return { samsClubStoreIds: selectedStores, lastSamsClubStoreChange: timestamp };
    case "bestbuy":
      return { bestbuyStoreIds: selectedStores, lastBestBuyStoreChange: timestamp };
    default:
      return null;
  }
}

function retailerDisplayName(retailer: string) {
  switch (retailer) {
    case "home_depot":
      return "HD";
    case "target":
      return "Target";
    case "dollar_general":
      return "Dollar General";
    case "costco":
      return "Costco";
    case "lowes":
      return "Lowe's";
    case "tractor_supply":
      return "Tractor Supply";
    case "northern_tool":
      return "Northern Tool";
    case "samsclub":
      return "Sam's Club";
    case "bestbuy":
      return "Best Buy";
    default:
      return "Walmart";
  }
}

function showStoreSaveError(error: unknown, retailerName: string) {
  const rateLimitMessage = parseStoreChangeError(error);
  if (rateLimitMessage) {
    toast.warn(rateLimitMessage, { position: "top-right", autoClose: 8000 });
    return;
  }
  // 400s carry the server's reason (e.g. "Your plan allows up to 3 store(s)…").
  const status = error && typeof error === "object" && "status" in error ? (error as { status: number }).status : undefined;
  const serverMessage = status === 400 && error instanceof Error ? error.message.trim() : "";
  if (serverMessage) {
    toast.error(serverMessage, { position: "top-right", autoClose: 8000 });
    return;
  }
  toast.error(`Failed to save ${retailerName} store selections. Please try again.`, {
    position: "top-right",
    autoClose: 4000,
  });
}

export default function StoresFilter({
  selectedStores: savedStores,
  storeLimit,
  retailer,
  loadedStores: externalStores,
  onStoreChange,
}: StoresFilterProps) {
  const { user, updateUser } = useUserContext();
  const { t } = useTranslation();
  // Only the stores the plan allots: a profile left over from a bigger plan
  // would otherwise be saved back whole and rejected by the server.
  const selectedStores = useMemo(
    () => allottedStoreIds(savedStores, storeLimit, user?.allowMoreStores),
    [savedStores, storeLimit, user?.allowMoreStores]
  );
  const { stores: hookStores, error: storesError } = useStores(
    retailer as "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco" | "lowes" | "tractor_supply" | "northern_tool" | "samsclub"
  );
  const [catalogStores, setCatalogStores] = useState<Store[] | null>(null);
  const stores = catalogStores ?? externalStores ?? hookStores;
  const retailerKey = retailer as RetailerWithStores;
  const storeChangeRetailer = retailer as RetailerType;

  const nonEmptyStoreCount = useMemo(
    () => selectedStores.filter((id) => id && String(id).trim() !== "").length,
    [selectedStores]
  );
  const activeSlotCount = useMemo(
    () =>
      user?.allowMoreStores
        ? Math.max(storeLimit, nonEmptyStoreCount)
        : storeLimit,
    [user?.allowMoreStores, storeLimit, nonEmptyStoreCount]
  );

  // State for store selection
  const [tempSelectedStoreIds, setTempSelectedStoreIds] = useState<string[]>(
    () => {
      // Initialize with selectedStores, padding with empty strings to reach active slot count
      const limit = storeLimit; // first paint; useEffect syncs to activeSlotCount
      const padded = [...selectedStores];
      while (padded.length < limit) {
        padded.push("");
      }
      return padded;
    }
  );
  const [zipCode, setZipCode] = useState("");
  const [userCoords, setUserCoords] = useState<{ lat: number; long: number } | null>(null);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [nearbyStores, setNearbyStores] = useState<StoreWithDistance[]>([]);
  const [activityByStore, setActivityByStore] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  // State for modal management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(
    null
  );
  const [modalSearchQuery, setModalSearchQuery] = useState("");
  const [tempSelectedStoreId, setTempSelectedStoreId] = useState<string>("");
  const [storeDetailsCache, setStoreDetailsCache] = useState<Record<string, StoreWithDistance>>({});
  const [isRefreshingCatalog, setIsRefreshingCatalog] = useState(false);
  const [refreshStatus, setRefreshStatus] = useState(() =>
    getStoresRefreshStatus(retailer as RetailerWithStores)
  );

  const storeChangeLock = useMemo(
    () => getWeeklyStoreChangeLock(user, storeChangeRetailer),
    [user, storeChangeRetailer]
  );
  const nextStoreChangeLabel = storeChangeLock?.nextAllowedDate.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  useEffect(() => {
    const update = () =>
      setRefreshStatus(getStoresRefreshStatus(retailer as RetailerWithStores));
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, [isModalOpen, retailer]);

  // Update tempSelectedStoreIds when selectedStores changes
  useEffect(() => {
    const padded = [...selectedStores];
    while (padded.length < activeSlotCount) {
      padded.push("");
    }
    setTempSelectedStoreIds(padded);
  }, [selectedStores, activeSlotCount]);

  // Initialize zip code with user's zip code
  useEffect(() => {
    if (user?.zipCode) {
      setZipCode(user.zipCode);
    } else {
      console.log("No user zip code available");
    }
  }, [user?.zipCode]);

  // Test store data loading immediately
  useEffect(() => {
    if (stores) {
      console.log("Testing store data loading for retailer:", retailer);
      const storeData = getStoreData(stores as Store[]);
      console.log(
        `Store data loaded: ${storeData.length} stores for ${retailer}`
      );
      if (storeData.length > 0) {
        console.log("Sample store:", storeData[0]);
      }
    }
    if (storesError) {
      console.error("Error loading stores:", storesError);
    }
  }, [retailer, stores, storesError]);

  // Geocode zip code and find nearby stores (skip stores without geo)
  const rebuildNearbyStores = useCallback(
    async (storeList: Store[] | null | undefined) => {
      const storeData = storeList ? getStoreData(storeList) : [];
      if (zipCode && /^\d{5}$/.test(zipCode)) {
        setIsGeocodingLoading(true);
        setNearbyStores([]);
        try {
          const coords = await geocodeZipCode(zipCode);
          setUserCoords(coords);
          if (!coords || storeData.length === 0) {
            setNearbyStores([]);
            return;
          }
          const nearbyStoresList = storeData
            .map((store) => normalizeStore(store))
            .filter(
              (store) =>
                store.normalizedGeo?.lat !== undefined &&
                store.normalizedGeo?.long !== undefined &&
                !isNaN(store.normalizedGeo.lat) &&
                !isNaN(store.normalizedGeo.long)
            )
            .map((store) => ({
              ...store,
              distance: calculateDistance(
                coords.lat,
                coords.long,
                store.normalizedGeo!.lat,
                store.normalizedGeo!.long
              ),
            }))
            .filter((store) => store.distance <= 100)
            .sort((a, b) => a.distance - b.distance)
            .slice(0, 50);
          setNearbyStores(nearbyStoresList);
        } catch (error) {
          console.error("Geocoding error:", error);
          setUserCoords(null);
          setNearbyStores([]);
        } finally {
          setIsGeocodingLoading(false);
        }
        return;
      }

      const normalizedStores = storeData
        .map((store) => normalizeStore(store))
        .filter(
          (store) =>
            store.normalizedGeo?.lat &&
            store.normalizedGeo?.long &&
            !isNaN(store.normalizedGeo.lat) &&
            !isNaN(store.normalizedGeo.long)
        );
      setNearbyStores(normalizedStores.slice(0, 50));
      setUserCoords(null);
      setIsGeocodingLoading(false);
    },
    [zipCode]
  );

  useEffect(() => {
    rebuildNearbyStores(stores as Store[] | null | undefined);
  }, [stores, rebuildNearbyStores]);

  // Close modal with escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isModalOpen) {
        setIsModalOpen(false);
        setSelectedSlotIndex(null);
        setModalSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isModalOpen]);

  const saveStoreSelections = async (selectedStoreIds: string[]) => {
    if (onStoreChange) {
      await onStoreChange(selectedStoreIds);
      return;
    }

    const updateData = buildStoreUpdatePayload(retailer, selectedStoreIds, Date.now());
    if (!updateData) {
      throw new Error(`Unsupported retailer for store save: ${retailer}`);
    }

    console.log("Saving store selections to database:", updateData);
    await updateUser(updateData);
    console.log("Store selections saved successfully");
  };

  const handleModalSave = async () => {
    if (selectedSlotIndex === null || !tempSelectedStoreId || isSaving) {
      return;
    }
    if (storeChangeLock?.isLocked) {
      toast.warn(
        `Store changes are limited to ${storeChangeLock.limitDescription}. You can change again on ${nextStoreChangeLabel}.`,
        { position: "top-right", autoClose: 8000 }
      );
      return;
    }

    const newSelectedStores = [...tempSelectedStoreIds];
    newSelectedStores[selectedSlotIndex] = tempSelectedStoreId;
    const selectedStoreIds = formatStoreIdsForSave(newSelectedStores, retailer, stores as Store[] | null);
    const selectedStore = nearbyStores.find(
      (store) => store.normalizedId === tempSelectedStoreId
    );

    try {
      setIsSaving(true);
      await saveStoreSelections(selectedStoreIds);
      setTempSelectedStoreIds(newSelectedStores);
      if (selectedStore) {
        setStoreDetailsCache((prev) => ({
          ...prev,
          [tempSelectedStoreId]: selectedStore,
        }));
      }
      toast.success(`${retailerDisplayName(retailer)} store saved!`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error saving store selections:", error);
      showStoreSaveError(error, retailerDisplayName(retailer));
    } finally {
      setIsSaving(false);
      setIsModalOpen(false);
      setSelectedSlotIndex(null);
      setModalSearchQuery("");
      setTempSelectedStoreId("");
    }
  };

  const openStoreModal = (slotIndex: number) => {
    if (storeChangeLock?.isLocked) {
      toast.warn(
        `Store changes are limited to ${storeChangeLock.limitDescription}. You can change again on ${nextStoreChangeLabel}.`,
        { position: "top-right", autoClose: 8000 }
      );
      return;
    }
    setSelectedSlotIndex(slotIndex);
    setIsModalOpen(true);
    setModalSearchQuery("");
    setTempSelectedStoreId(tempSelectedStoreIds[slotIndex] || "");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSlotIndex(null);
    setModalSearchQuery("");
    setTempSelectedStoreId("");
  };

  const handleRefreshStores = async () => {
    if (!refreshStatus.canRefresh || isRefreshingCatalog) return;
    setIsRefreshingCatalog(true);
    try {
      const current = getStoreData((stores ?? []) as Store[]);
      const { stores: fresh, newStoreIds } = await refreshStoresCatalog(
        retailerKey,
        current as Store[]
      );
      setCatalogStores(fresh);
      await rebuildNearbyStores(fresh);
      setRefreshStatus(getStoresRefreshStatus(retailerKey));
      if (newStoreIds.length > 0) {
        toast.success(
          `${newStoreIds.length} new store${newStoreIds.length === 1 ? "" : "s"} added to the list`
        );
      } else {
        toast.info("No new stores");
      }
    } catch (error) {
      if (error instanceof RefreshCooldownError) {
        setRefreshStatus(getStoresRefreshStatus(retailerKey));
        toast.info(`Refresh again in ${formatRefreshCooldown(error.remainingMs)}`);
      } else {
        toast.error("Failed to refresh store list");
      }
    } finally {
      setIsRefreshingCatalog(false);
    }
  };

  const modalStorePool = useMemo(() => {
    if (!modalSearchQuery.trim()) return nearbyStores;
    const catalog = getStoreData((stores ?? []) as Store[]).map((store) =>
      normalizeStore(store)
    );
    const nearbyById = new Map(nearbyStores.map((store) => [store.normalizedId, store]));
    return catalog.map((store) => nearbyById.get(store.normalizedId) ?? store);
  }, [modalSearchQuery, nearbyStores, stores]);

  const activityStoreIds = useMemo(() => {
    const ids = new Set(selectedStores.map((id) => String(id).trim()).filter(Boolean));
    for (const store of modalStorePool) {
      if (ids.size >= 100) break;
      if (store.normalizedId) ids.add(store.normalizedId);
    }
    return [...ids];
  }, [modalStorePool, selectedStores]);

  useEffect(() => {
    let cancelled = false;
    void getStoreActivity(storeChangeRetailer, activityStoreIds)
      .then((activity) => {
        if (!cancelled) setActivityByStore(activity);
      })
      .catch((error) => {
        console.warn("Unable to load store activity:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [activityStoreIds, storeChangeRetailer]);

  const refreshButtonLabel = isRefreshingCatalog
    ? "Refreshing..."
    : refreshStatus.canRefresh
      ? "Refresh stores"
      : `Available in ${formatRefreshCooldown(refreshStatus.remainingMs)}`;

  const handleZipCodeChange = (newZipCode: string) => {
    console.log("Zip code changed from", zipCode, "to", newZipCode);
    setZipCode(newZipCode);
  };

  const getFilteredStores = () => {
    const currentStoreId =
      selectedSlotIndex !== null ? tempSelectedStoreIds[selectedSlotIndex] : "";

    console.log("Modal filtering - nearbyStores count:", nearbyStores.length);
    console.log("Modal filtering - search query:", modalSearchQuery);
    console.log("Modal filtering - current store ID:", currentStoreId);
    console.log(
      "Modal filtering - tempSelectedStoreIds:",
      tempSelectedStoreIds
    );
    console.log("Modal filtering - selectedSlotIndex:", selectedSlotIndex);
    if (nearbyStores.length > 0) {
      console.log("Modal filtering - first store:", nearbyStores[0]);
    }

    const filtered = modalStorePool
      .filter((store) => {
        const matchesSearch =
          !modalSearchQuery ||
          store.normalizedId
            .toLowerCase()
            .includes(modalSearchQuery.toLowerCase()) ||
          store.normalizedAddress
            .toLowerCase()
            .includes(modalSearchQuery.toLowerCase());

        // Allow current store to be shown (for switching)
        const isNotCurrentStore = store.normalizedId !== currentStoreId;

        // Exclude stores that are selected in OTHER slots (not the current one)
        // Skip empty storeIds so unfilled slots don't match stores with empty normalizedId
        const isNotSelectedInOtherSlots = !tempSelectedStoreIds.some(
          (storeId, index) =>
            storeId !== "" && storeId === store.normalizedId && index !== selectedSlotIndex
        );

        const shouldInclude =
          matchesSearch && isNotCurrentStore && isNotSelectedInOtherSlots;

        // Debug first few stores
        if (nearbyStores.indexOf(store) < 3) {
          console.log(
            `Store ${store.normalizedId}: matchesSearch=${matchesSearch}, isNotCurrentStore=${isNotCurrentStore}, isNotSelectedInOtherSlots=${isNotSelectedInOtherSlots}, shouldInclude=${shouldInclude}`
          );
        }

        return shouldInclude;
      })
      .slice(0, 100); // Show more results in modal

    console.log("Modal filtering - filtered count:", filtered.length);
    return filtered;
  };

  const resolveStoreForSlot = useCallback(
    (storeId: string): StoreWithDistance | null => {
      if (!storeId) return null;
      if (storeDetailsCache[storeId]) return storeDetailsCache[storeId];

      const nearbyMatch = nearbyStores.find((store) => store.normalizedId === storeId);
      if (nearbyMatch) return nearbyMatch;

      const storeData = stores ? getStoreData(stores as Store[]) : [];
      if (retailer === "dollar_general" && userCoords) {
        const match = findDollarGeneralStoreInList(
          storeData as DollarGeneralStore[],
          storeId,
          userCoords
        );
        return match ? normalizeStore(match) : null;
      }

      const match = storeData.find(
        (store) => normalizeStore(store).normalizedId === storeId
      );
      return match ? normalizeStore(match) : null;
    },
    [nearbyStores, stores, retailer, userCoords, storeDetailsCache]
  );

  // Create slots array
  const slots = Array.from({ length: activeSlotCount }, (_, index) => {
    const storeId = tempSelectedStoreIds[index] || "";
    const store = storeId ? resolveStoreForSlot(storeId) : null;

    return {
      index,
      storeId,
      store,
    };
  });

  const pendingStoreChangeWarning = useMemo(() => {
    if (selectedSlotIndex === null || !tempSelectedStoreId) return null;
    if (tempSelectedStoreIds[selectedSlotIndex] === tempSelectedStoreId) return null;
    const nextStores = [...tempSelectedStoreIds];
    nextStores[selectedSlotIndex] = tempSelectedStoreId;
    return getStoreChangeWarning(
      user,
      storeChangeRetailer,
      Date.now(),
      formatStoreIdsForSave(nextStores, retailer, stores as Store[] | null)
    );
  }, [selectedSlotIndex, storeChangeRetailer, tempSelectedStoreId, tempSelectedStoreIds, user, retailer, stores]);
  const pendingStoreChangeLabel = pendingStoreChangeWarning?.nextAllowedDate.toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Zip Code Display */}
      <ZipCodeDisplay zipCode={zipCode} onZipCodeChange={handleZipCodeChange} variant="flat" />

      {storeChangeLock?.isLocked && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Store changes are limited to {storeChangeLock.limitDescription}. You can change again on {nextStoreChangeLabel}.
        </div>
      )}

      {/* Store Slots */}
      <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-dark-700">
          {slots.map((slot, i) => {
            const hasStore = !!slot.store;
            return (
              <button
                key={slot.index}
                type="button"
                onClick={() => openStoreModal(slot.index)}
                disabled={isGeocodingLoading}
                className={`group w-full border-b border-gray-200 text-left transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50 last:border-b-0 dark:border-dark-700 ${
                  hasStore
                    ? "bg-white hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-900/40"
                    : "bg-white hover:bg-gray-50 dark:bg-dark-800 dark:hover:bg-dark-900/40"
                }`}
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <div className="flex items-center gap-3 px-4 py-3 sm:gap-3.5">
                  <div
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border text-[11px] font-semibold transition-colors ${
                      hasStore
                        ? "border-primary-200 bg-primary-50 text-primary-700 dark:border-primary-900/60 dark:bg-primary-900/20 dark:text-primary-300"
                        : "border-gray-200 bg-gray-50 text-gray-500 dark:border-dark-600 dark:bg-dark-900 dark:text-dark-300"
                    }`}
                  >
                    {slot.index + 1}
                  </div>

                  <div className="min-w-0 flex-1">
                    {hasStore ? (
                      <>
                        <div className="mb-0.5 flex items-center gap-1.5">
                          <span className="text-[11px] font-medium text-gray-500 dark:text-dark-300">
                            Store {slot.index + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            #{slot.store!.normalizedId}
                          </span>
                          {slot.store!.distance && (
                            <span className="text-[11px] font-medium text-gray-400 dark:text-dark-400">
                              {slot.store!.distance.toFixed(1)} mi
                            </span>
                          )}
                        </div>
                        <p className="truncate text-[13px] text-gray-700 dark:text-dark-200 sm:text-sm">
                          {slot.store!.normalizedAddress}
                        </p>
                        {storeActivityLabel(activityByStore[slot.storeId], t) && (
                          <p className={`mt-1 text-[11px] font-medium ${
                            activityByStore[slot.storeId]
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-amber-600 dark:text-amber-400"
                          }`}>
                            {storeActivityLabel(activityByStore[slot.storeId], t)}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-700 dark:text-dark-200">
                          {isGeocodingLoading ? `Store ${slot.index + 1}` : `Select store ${slot.index + 1}`}
                        </p>
                        <p className="mt-0.5 text-[12px] text-gray-500 dark:text-dark-400">
                          {isGeocodingLoading ? "Finding nearby stores..." : "No store selected"}
                        </p>
                      </>
                    )}
                  </div>

                  <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-500 dark:text-dark-500 dark:group-hover:text-dark-300" />
                </div>
              </button>
            );
          })}
      </div>

      {/* Store Selection Modal */}
      {isModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/40 p-0 dark:bg-black/60 sm:items-center sm:p-4 sheet-backdrop-in">
            <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl dark:bg-dark-800 sm:max-h-[75vh] sm:rounded-2xl sheet-panel-in-sm">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 flex-shrink-0">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                    Choose a Store
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-400">
                    Replace Store {selectedSlotIndex == null ? "" : selectedSlotIndex + 1}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="ml-2 flex-shrink-0 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-dark-400 dark:hover:bg-dark-700 dark:hover:text-dark-200"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>

              {/* Search Input */}
              <div className="px-4 sm:px-5 pb-2 flex-shrink-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-dark-400" />
                  <input
                    type="text"
                    placeholder="Search address or store #"
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 text-sm border border-gray-200 dark:border-dark-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-gray-50 dark:bg-dark-700 dark:text-white dark:placeholder-dark-400 transition-all duration-200"
                  />
                </div>
                <div className="mt-1.5 flex justify-end">
                  <button
                    type="button"
                    onClick={handleRefreshStores}
                    disabled={!refreshStatus.canRefresh || isRefreshingCatalog}
                    className="inline-flex items-center gap-1 text-[11px] text-gray-400 transition-colors hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-dark-500 dark:hover:text-dark-300"
                  >
                    <RefreshCw className={`h-3 w-3 ${isRefreshingCatalog ? "animate-spin" : ""}`} />
                    {refreshButtonLabel}
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 dark:border-dark-700" />

              {/* Store List */}
              <div className="overflow-y-auto flex-1 scrollbar-modern">
                {(() => {
                  const filteredStores = getFilteredStores();
                  console.log("Modal rendering - filteredStores count:", filteredStores.length);

                  if (isGeocodingLoading) {
                    return (
                      <div className="flex items-center justify-center py-12 sm:py-16">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-7 h-7 border-2 border-primary-200 dark:border-primary-800 border-t-primary-600 dark:border-t-primary-400 rounded-full animate-spin" />
                          <span className="text-sm text-gray-500 dark:text-dark-400">Finding stores...</span>
                        </div>
                      </div>
                    );
                  } else if (nearbyStores.length > 0 && filteredStores.length > 0) {
                    return (
                      <div className="divide-y divide-gray-100 dark:divide-dark-700">
                        {filteredStores.map((store) => {
                          const isSelected = store.normalizedId === tempSelectedStoreId;
                          return (
                            <button
                              key={`${store.normalizedId}:${store.normalizedAddress}`}
                              onClick={() => setTempSelectedStoreId(store.normalizedId)}
                              className={`group/item flex w-full items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                                isSelected
                                  ? "bg-primary-50/70 dark:bg-primary-900/20"
                                  : "hover:bg-gray-50 dark:hover:bg-dark-700/50"
                              }`}
                            >
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                                isSelected
                                  ? "border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500"
                                  : "border-gray-300 dark:border-dark-500 group-hover/item:border-gray-400 dark:group-hover/item:border-dark-400"
                              }`}>
                                {isSelected && <Check className="h-3 w-3 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <span className={`text-xs font-semibold ${
                                    isSelected
                                      ? "text-primary-700 dark:text-primary-300"
                                      : "text-gray-600 dark:text-dark-300"
                                  }`}>
                                    #{store.normalizedId}
                                  </span>
                                  {store.distance && (
                                    <span className="text-[11px] text-gray-400 dark:text-dark-400 font-medium flex items-center gap-0.5">
                                      <MapPin className="h-2.5 w-2.5" />
                                      {store.distance.toFixed(1)} mi
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs sm:text-sm text-gray-700 dark:text-dark-200 leading-relaxed truncate">
                                  {store.normalizedAddress}
                                </p>
                                {storeActivityLabel(activityByStore[store.normalizedId], t) && (
                                  <p className={`mt-0.5 text-[11px] font-medium ${
                                    activityByStore[store.normalizedId]
                                      ? "text-emerald-600 dark:text-emerald-400"
                                      : "text-amber-600 dark:text-amber-400"
                                  }`}>
                                    {storeActivityLabel(activityByStore[store.normalizedId], t)}
                                  </p>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    );
                  } else {
                    const isSearchEmpty = nearbyStores.length > 0 && filteredStores.length === 0;
                    return (
                      <div className="p-8 sm:p-12 text-center">
                        <div className="w-12 h-12 bg-gray-100 dark:bg-dark-700 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          {isSearchEmpty ? <Search className="h-5 w-5 text-gray-400 dark:text-dark-400" /> : <MapPin className="h-5 w-5 text-gray-400 dark:text-dark-400" />}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-dark-300 font-medium mb-1">
                          {isSearchEmpty && modalSearchQuery ? "No matching stores" : "No stores found within 100 miles"}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-dark-500">
                          {isSearchEmpty && modalSearchQuery ? "Try a different search term" : "Try a different zip code to find stores"}
                        </p>
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Modal Footer */}
              <div className="border-t border-gray-100 dark:border-dark-700 px-4 sm:px-5 py-3 sm:py-4 flex-shrink-0">
                {pendingStoreChangeWarning && (
                  <div className="mb-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100">
                    <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/50">
                      <Clock3 className="h-4 w-4 text-amber-700 dark:text-amber-300" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Before you change stores</p>
                      <p className="mt-0.5 text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                        After this change, you won&apos;t be able to change your {retailerDisplayName(retailer)} stores again until {pendingStoreChangeLabel}.
                      </p>
                      <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                        Your plan allows store changes {pendingStoreChangeWarning.limitDescription}.
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex gap-2.5">
                  <button
                    onClick={closeModal}
                    className="flex-1 px-4 py-2 sm:py-2.5 text-sm font-medium text-gray-700 dark:text-dark-200 bg-gray-100 dark:bg-dark-700 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleModalSave}
                    disabled={!tempSelectedStoreId || isSaving || !!storeChangeLock?.isLocked}
                    className="flex-1 px-4 py-2 sm:py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
